import React, { useCallback, useEffect, useState } from "react";
import Button from "../../../components/Common/Button/Button";
import DataTable from "../../../components/Common/DataTable/DataTable";
import Input from "../../../components/Common/Input/Input";
import AddIcon from "../../../components/Common/SvgIcons/AddIcon";
import { useFetchData } from "../../../hooks/useFetchData";
import {
  addTrainingTypeActionHandler,
  deleteTrainingTypeActionHandler,
  getTrainingTypesActionHandler,
  isTrainingTypeExistsActionHandler,
} from "../../../store/actions/actionHandlers/manageActionHandler";
import { FormProvider, useForm } from "react-hook-form";
import { createTrainingType } from "../../../utils/mainTabs/manage/createTrainingType";
import { useDispatch, useSelector } from "react-redux";
import Modal from "../../../components/Common/Modal/Modal";
import { AddTrainingTypeSchema } from "../../../utils/mainTabs/manage/createTrainingTypeSchema";
import { yupResolver } from "@hookform/resolvers/yup";
import EditTrainingTypeModal from "../../../components/Manage/EditTrainingTypeModal/EditTrainingTypeModal";
import classes from "./CreateTrainingType.module.scss";
import { errorTypes } from "../../../utils/errorTypes";
import { setErrorHandler } from "../../../store/actions/actionHandlers/errorActionHandler";
import { FormattedMessage, injectIntl } from "react-intl";

const CreateTrainingType = ({intl}) => {
  const pageSize = 8;
  const dispatch = useDispatch();
  const manageState = useSelector((state) => state.manageReducer);
  const { addTrainingTypeLoader, deleteTrainingTypeLoader } = manageState;

  const [tableColumns, setTableColumns] = useState([]);
  const [isAddTrainingNameExists, setIsAddTrainingNameExists] = useState(null);
  const [addTrainingNameExistsLoader, setAddTrainingNameExistsLoader] =
    useState(false);
  const [deleteTrainingTypeId, setDeleteTrainingTypeId] = useState(0);
  const [deleteTrainingTypeName, setDeleteTrainingTypeName] = useState("");
  const [editTrainingTypeId, setEditTrainingTypeId] = useState(0);
  const [editTrainingTypeName, setEditTrainingTypeName] = useState("");

  const addTrainingTypeMethods = useForm({
    defaultValues: { AddTrainingType: "" },
    resolver: yupResolver(AddTrainingTypeSchema),
    mode: "onChange",
  });

  const {
    handleSubmit: addTrainingTypeHandleSubmit,
    reset: addTrainingTypeReset,
    formState: {
      isValid: isAddTrainingTypeValid,
      errors: addTrainingTypeErrors,
    },
  } = addTrainingTypeMethods;

  const { searchPlaceHolder, columns } = createTrainingType;
  const {
    data,
    setData,
    updatedData,
    setUpdatedData,
    loading,
    totalRows,
    handleCurrentPage,
    handleSearchQuery,
  } = useFetchData(getTrainingTypesActionHandler, pageSize);

  const handleAddTrainingNameExists = useCallback(
    (e) => {
      let val = e.target.value.trim() !== "" && e.target.value.trim();
      if (val) {
        if (/^[a-zA-Z0-9:_@?#|'`\]\[ ]*$/.test(val)) {
          setAddTrainingNameExistsLoader(true);
          dispatch(isTrainingTypeExistsActionHandler(val)).then((response) => {
            if (response && response.status === 200) {
              setAddTrainingNameExistsLoader(false);
              if (response.data) {
                setIsAddTrainingNameExists(true);
              } else {
                setIsAddTrainingNameExists(false);
              }
            }
          });
        }
      } else {
        setIsAddTrainingNameExists(false);
      }
    },
    [dispatch, setIsAddTrainingNameExists]
  );

  const handleEditModal = useCallback((trainingTypeId, trainingTypeName) => {
    setEditTrainingTypeId(trainingTypeId);
    setEditTrainingTypeName(trainingTypeName);
  }, []);
  const handleDeleteModal = useCallback((trainingTypeId, trainingTypeName) => {
    setDeleteTrainingTypeId(trainingTypeId);
    setDeleteTrainingTypeName(trainingTypeName);
  }, []);

  const handleDeleteTrainingType = () => {
    dispatch(deleteTrainingTypeActionHandler(deleteTrainingTypeId)).then(
      (isDeleted) => {
        if (isDeleted) {
          const filteredData = data.filter(
            (row) => row.TypeId !== deleteTrainingTypeId
          );
          const filteredUpdatedData = updatedData.filter(
            (row) => row.TypeId !== deleteTrainingTypeId
          );
          setData(filteredData);
          setUpdatedData(filteredUpdatedData);
          dispatch(
            setErrorHandler(
              errorTypes.SUCCESS,
              `Training Type ${deleteTrainingTypeName} deleted successfully`
            )
          );
        }
        handleDeleteModal(0, "");
      }
    );
  };

  //updating columns for training types
  useEffect(() => {
    let newTabelColumns = columns(handleDeleteModal, handleEditModal);
    setTableColumns(newTabelColumns);
  }, [columns, handleDeleteModal, handleEditModal]);

  //add training type logic
  const onAddSubmit = (data) => {
    dispatch(addTrainingTypeActionHandler(data.AddTrainingType, 0)).then(
      (isAdded) => {
        if (isAdded) {
          dispatch(
            setErrorHandler(
              errorTypes.SUCCESS,
              `Training Type ${data.AddTrainingType} created successfully`
            )
          );
          handleSearchQuery();
          addTrainingTypeReset();
        }
      }
    );
  };

  return (
    <div className={classes.createTrainingType}>
      <FormProvider {...addTrainingTypeMethods}>
        <form
          onSubmit={addTrainingTypeHandleSubmit(onAddSubmit)}
          className={classes.createTrainingType__form}
        >
          <div className={classes.createTrainingType__form__addField}>
            <Input
              name={"AddTrainingType"}
              placeholder={intl.formatMessage({
                id: `ENTER_TRAINING_TYPE`,
              })}
              height="5"
              width="53"
              gap="0"
              onChange={(e) => handleAddTrainingNameExists(e)}
              loading={addTrainingNameExistsLoader && !editTrainingTypeId}
              // error={addTrainingTypeErrors?.["AddTrainingType"]?.message}
            />
            {addTrainingTypeErrors?.AddTrainingType?.message ? (
              <div
                className={classes.createTrainingType__form__addField__error}
              >
                {addTrainingTypeErrors?.AddTrainingType?.message}
              </div>
            ) : (
              isAddTrainingNameExists && (
                <div
                  className={classes.createTrainingType__form__addField__error}
                >
                  <FormattedMessage id="TRAINING_NAME_EXIST" />
                </div>
              )
            )}
          </div>
          <Button
            label={"Add Training"}
            disabled={!isAddTrainingTypeValid || isAddTrainingNameExists}
            icon={AddIcon}
            type="submit"
            height="5"
            width="15.5"
            light
            loading={addTrainingTypeLoader && !editTrainingTypeId}
          />
        </form>
      </FormProvider>
      <DataTable
        columns={tableColumns}
        defaultData={data}
        setDefaultData={setData}
        data={updatedData}
        setData={setUpdatedData}
        totalRows={totalRows}
        loading={loading}
        uniqueFieldName={"TypeId"}
        fetchMoreData={handleCurrentPage}
        filterable
        setSearchQuery={handleSearchQuery}
        placeholder={searchPlaceHolder}
      />
      <Modal
        handleSubmit={handleDeleteTrainingType}
        isOpen={deleteTrainingTypeId}
        heading={<FormattedMessage id="USERGROUP_DELETEGRP__HEADERMSG" />}
        toggle={() => handleDeleteModal(0, "")}
        buttonLabel1={<FormattedMessage id="YES_TEXT" />}
        buttonLabel2={<FormattedMessage id="USERGRP_CANCELBTN_TEXT" />}
        width={"46.2"}
        height={"26.9"}
        loading={deleteTrainingTypeLoader}
      >
        <div className={classes.createTrainingType__deleteModal}>
          <div className={classes.createTrainingType__deleteModal__text}>
            <FormattedMessage id="TRAINING_DEL_CONFIRM" />
          </div>
        </div>
      </Modal>
      <EditTrainingTypeModal
        editTrainingTypeId={editTrainingTypeId}
        editTrainingTypeName={editTrainingTypeName}
        handleSearchQuery={handleSearchQuery}
        handleEditModal={handleEditModal}
      />
    </div>
  );
};

export default injectIntl(CreateTrainingType);
